import { motion, useInView } from "framer-motion";
import { useRef } from "react";

/**
 * Splits text into words and reveals them with a mask-up stagger.
 * Accepts text as `lines` (array of strings) or `children` (single string).
 *
 * Each word sits in an overflow-hidden box; the inner span translates from
 * 110% down to 0 with a tiny per-word delay.
 */
export default function SplitReveal({
  lines,
  className = "",
  wordClassName = "",
  delay = 0,
  duration = 0.9,
  stagger = 0.05,
  as: Tag = "h2",
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -10% 0px" });

  const variants = {
    hidden: { y: "115%" },
    visible: (i) => ({
      y: "0%",
      transition: {
        duration,
        ease: [0.22, 1, 0.36, 1],
        delay: delay + i * stagger,
      },
    }),
  };

  let counter = 0;

  return (
    <Tag ref={ref} className={className}>
      {lines.map((line, li) => (
        <span
          key={li}
          className="block"
          style={{ overflow: "hidden", paddingTop: "0.15em", paddingBottom: "0.28em" }}
        >
          {line.split(" ").map((word, wi) => {
            const idx = counter++;
            return (
              <span
                key={`${li}-${wi}`}
                className="inline-block overflow-hidden"
                style={{ lineHeight: "1.2" }}
              >
                <motion.span
                  className={`inline-block ${wordClassName}`}
                  custom={idx}
                  initial="hidden"
                  animate={inView ? "visible" : "hidden"}
                  variants={variants}
                  style={{ willChange: "transform" }}
                >
                  {word}
                  {wi < line.split(" ").length - 1 ? "\u00A0" : ""}
                </motion.span>
              </span>
            );
          })}
        </span>
      ))}
    </Tag>
  );
}
